<template>
  <footer class="footer">
    <div class="footer__forms">
      <GeneralFooterSubmitCV v-if="!showSubmitCV" />
      <GeneralFooterNewsletter
        v-if="Object.keys(state.newsletterBlok).length"
        :blok="state.newsletterBlok"
      />
    </div>
    <GeneralFooterAwards
      v-if="Object.keys(state.newsletterBlok).length"
      :blok="state.awardsBlok"
    />
    <div
      v-if="Object.keys(state.navigation).length"
      class="footer__links"
    >
      <Link
        v-if="state.navigation?.cookiePolicy"
        :blok="state.navigation.cookiePolicy"
      >
        {{ $t('footer.cookiePolicy.label') }}
      </Link>
      <Link
        v-if="state.navigation?.privacyPolicy"
        :blok="state.navigation.privacyPolicy"
      >
        {{ $t('footer.privacyPolicy.label') }}
      </Link>
      <Link
        v-if="state.navigation?.leadershipPrivacyPolicy?.story"
        :blok="state.navigation.leadershipPrivacyPolicy"
      >
        {{ $t('footer.leadershipPrivacyPolicy.label') }}
      </Link>
    </div>
    <div class="footer__bottom">
      <div class="content">
        <div class="footer__left">
          <div class="menu">
            <NuxtLink
              class="body-3"
              :to="`https://www.airbaltic.com/${locale}/index`"
              @click="handleAirbaltic"
            >
              www.airbaltic.com
            </NuxtLink>
            <NuxtLink
              class="body-3"
              :to="`/${locale}/${t('contacts.code')}`"
              @click="handleContactUs"
            >
              {{ $t('contacts.title') }}
            </NuxtLink>
          </div>
          <div class="copyright">
            <span>{{ new Date().getFullYear() }} © airBaltic. All rights reserved.</span>
          </div>
        </div>
        <GeneralFooterSocials />
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
  import Link from '~/storyblok/Generic/Link.vue'
  import { useFetchFooter } from '~/composables/useFetchFooter'
  import { useAppStore } from '~/stores'

  const { locale, t } = useI18n()
  const route = useRoute()
  const { fetchFooter } = useFetchFooter()

  const appStore = useAppStore()

  const state = reactive<any>({
    navigation: {},
    newsletterBlok: {},
    awardsBlok: {}
  })

  fetchFooter(locale.value).then((response) => {
    state.navigation = response
    state.newsletterBlok = {
      privacyPolicy: response.privacyPolicy
    }
    state.awardsBlok = {
      text: response.awardText,
      images: response.awardImages
    }
  })

  watch(locale, () => {
    fetchFooter(locale.value).then((response) => {
      state.navigation = response
      state.newsletterBlok = {
        privacyPolicy: response.privacyPolicy
      }
      state.awardsBlok = {
        text: response.awardText,
        images: response.awardImages
      }
    })
  })

  const showSubmitCV = computed(() => {
    if (appStore.disableCVBlock) {
      return true
    }

    return (
      route.path.includes(t('talentPool.code')) ||
      route.path.includes(`/${t('vacancies.code')}/`) ||
      route.path.includes('teams/pilot-academy') ||
      route.path.includes('komandas/pilotu-akademija') ||
      route.path.includes('events') ||
      route.path.includes('pasakumi')
    )
  })

  const handleAirbaltic = () => {
    GTM.pressAirBaltic()
  }

  const handleContactUs = () => {
    GTM.pressContactUs()
  }
</script>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .footer {
    background-color: $bg-grey;

    @include tablet-up {
      background-color: transparent;
      margin-top: rem(96);
    }

    &__content {
      margin: auto;
      max-width: 1120px;
    }

    &__forms {
      margin: 0 auto rem(40);
      max-width: 1120px;
      padding: rem(40) rem(24);

      @include tablet-up {
        display: flex;
        flex-direction: row;
        justify-content: center;
        margin: auto auto rem(40);
        padding: 0 rem(24);
      }

      @include desktop-up {
        margin-bottom: rem(80);
      }

      :deep(.container) {
        justify-content: flex-start;
        width: calc(100% - 40px * 2);

        @include tablet-up {
          align-items: center;
          max-width: calc(50% - 16px);
        }

        &:not(:last-child) {
          margin-bottom: rem(32);

          @include tablet-up {
            margin: 0 rem(32) 0 0;
          }
        }
      }
    }

    &__links {
      display: none;
      margin: 0 auto;
      max-width: 1120px;

      @include tablet-up {
        display: block;
        padding: rem(40) rem(24);
      }

      a {
        font-size: rem(14);
        line-height: rem(16);
        margin-right: rem(24);

        &:last-child {
          margin-right: 0;
        }
      }
    }

    &__bottom {
      background-color: $bt-blue;
      color: $white;

      .content {
        align-items: center;
        display: flex;
        flex-direction: column;
        margin: auto;
        max-width: 1120px;
        padding: rem(20) rem(24) rem(24);
        width: calc(100% - 48px);

        @include tablet-up {
          padding: rem(26) rem(80);
          width: calc(100% - 160px);
        }

        @include desktop-up {
          flex-direction: row-reverse;
          padding: rem(40) rem(160);
          width: calc(100% - 320px);
        }
      }
    }

    &__left {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      order: 1;

      @include desktop-up {
        flex-direction: row-reverse;
        flex-wrap: wrap;
        justify-content: space-between;
        margin-right: 38px;
      }

      .menu {
        display: flex;
        justify-content: space-between;
        margin: rem(20) auto;
        max-width: 300px;
        width: 100%;

        a {
          color: $white;
        }
      }

      .copyright {
        font-size: 13px;
        font-weight: 400;
        flex-grow: 1;
        line-height: rem(40);
        margin: auto;
        text-align: center;

        @include tablet-up {
          min-width: 210px;
        }

        @include desktop-up {
          text-align: left;
        }
      }
    }
  }
</style>
